import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/layout/Header";
import { ProTerminalLayout } from "@/components/trading/ProTerminalLayout";
import { Helmet } from "react-helmet-async";
import { AdvancedTradingPanel } from "@/components/trading/AdvancedTradingPanel";
import { UniversalTradingPanel } from "@/components/trading/UniversalTradingPanel";
import { AdvancedChart } from "@/components/trading/AdvancedChart";
import { CoinInfo } from "@/components/trading/CoinInfo";
import { CoinChat } from "@/components/trading/CoinChat";
import { VotingPanel } from "@/components/trading/VotingPanel";
import { ExternalTokenVoting } from "@/components/discovery/ExternalTokenVoting";
import { OrderBook } from "@/components/trading/OrderBook";
import { TradeHistory } from "@/components/trading/TradeHistory";
import { HoldersTable } from "@/components/trading/HoldersTable";
import { TransactionHistoryPanel } from "@/components/trading/TransactionHistoryPanel";
import { WhaleTracker } from "@/components/social/WhaleTracker";
import { FloatingTradeWidget } from "@/components/trading/FloatingTradeWidget";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import {
  Globe,
  Zap,
  BarChart3,
  Info,
  MessageSquare,
  Users,
  Activity,
  Maximize2,
  Minimize2,
  PanelRightOpen,
  PanelRightClose
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function CoinDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("chart");
  const [showFloatingWidget, setShowFloatingWidget] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { data: coin, isLoading } = useQuery({
    queryKey: ["coin", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coins")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Subscribe to real-time coin updates
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel(`coin-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'coins',
          filter: `id=eq.${id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["coin", id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, queryClient]);

  // Handle fullscreen
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isFullscreen]);

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Loading... - TheMoonRun</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="p-2 lg:p-4">
            <div className="flex gap-4">
              <div className="flex-1 space-y-4">
                <Skeleton className="h-[60px]" />
                <Skeleton className="h-[500px]" />
              </div>
              <Skeleton className="w-[340px] h-[600px] hidden lg:block" />
            </div>
          </main>
        </div>
      </>
    );
  }

  if (!coin) {
    return (
      <>
        <Helmet>
          <title>Coin Not Found - TheMoonRun</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="flex-1 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-2">Coin Not Found</h1>
              <p className="text-muted-foreground">The coin you're looking for doesn't exist.</p>
            </div>
          </main>
        </div>
      </>
    );
  }

  const isExternalToken = coin.status === "external" && coin.mint_address;
  const TradingPanel = isExternalToken ? UniversalTradingPanel : AdvancedTradingPanel;

  return (
    <>
      <Helmet>
        <title>{coin.name} (${coin.ticker}) - TheMoonRun</title>
        <meta name="description" content={coin.description || `Trade ${coin.name} on TheMoonRun`} />
        {/* SEO Tags */}
        <meta property="og:title" content={`${coin.name} ($${coin.ticker}) | Pro Terminal`} />
        <meta property="og:description" content={`Real-time trading and charts for ${coin.name} on MoonRun.`} />
        <meta property="og:image" content={coin.image || "https://moonrun.app/og-image.png"} />
      </Helmet>

      <div className="min-h-screen bg-black flex flex-col">
        <Header />
        <ProTerminalLayout coin={coin} />
      </div>
    </>
  );
}
